import { useEffect, useMemo, useState } from "react";
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { DataTable } from "@/components/ui/data-table";
import { DataTablePagination } from "@/components/ui/table-pagination";
import { displayErrorMessage, getCountryObjectFromCache } from "@/utils";
import { getCurrentLanguageLabel } from "@/utils/i18n";

import { createColumns } from "./components/columns";
import { DEFAULT_PAGE_SIZE } from "./components/constants";
import { type ListProps } from "./components/types";
import {
  buildCommodityStatsMap,
  filterCommodities,
  getCommodityRowId,
  getLandUnitLabel,
  isCommodityAvailableToCountry,
} from "./components/utils";

export type { Commodity, CommodityListItem } from "./components/types";
export { COMMODITY_SORT_OPTIONS } from "./components/constants";

export const List = ({
  commodities = [],
  countryDashboardData,
  errorMessage = "",
  isFetching = false,
  onClickAddCommodity,
  onClickRemoveCommodity,
  onSortingChange,
  pageSize = DEFAULT_PAGE_SIZE,
  searchTerm = "",
  showAll = false,
  sorting,
  unitsOfMeasure = [],
}: ListProps) => {
  const { i18n, t } = useTranslation();
  const currentLanguageLabel = useMemo(
    () => getCurrentLanguageLabel(i18n.language),
    [i18n.language]
  );
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize > 0 ? pageSize : DEFAULT_PAGE_SIZE,
  });
  const resolvedSorting = sorting ?? internalSorting;
  const handleSortingChange = onSortingChange ?? setInternalSorting;
  const currentCountry = getCountryObjectFromCache();
  const statsMap = useMemo(
    () => buildCommodityStatsMap(countryDashboardData),
    [countryDashboardData]
  );
  const landUnitLabel = useMemo(
    () => getLandUnitLabel(countryDashboardData),
    [countryDashboardData]
  );
  const columns = useMemo(
    () =>
      createColumns(t, {
        currentCountry,
        currentLanguageCode: i18n.language,
        currentLanguageLabel,
        landUnitLabel,
        onClickAddCommodity,
        onClickRemoveCommodity,
        showAll,
        statsMap,
        unitsOfMeasure,
      }),
    [
      currentCountry,
      currentLanguageLabel,
      i18n.language,
      landUnitLabel,
      onClickAddCommodity,
      onClickRemoveCommodity,
      showAll,
      statsMap,
      t,
      unitsOfMeasure,
    ]
  );
  const filteredCommodities = useMemo(() => {
    const filtered = filterCommodities(commodities, searchTerm, i18n.language);

    if (!showAll) {
      return filtered;
    }

    // Manage view default order: commodities already active in the country first, keeping
    // the alphabetical order within each group (Array.prototype.sort is stable). Any column
    // sort the user picks overrides this.
    return [...filtered].sort((first, second) => {
      const firstRank = isCommodityAvailableToCountry(first, currentCountry?.id) ? 0 : 1;
      const secondRank = isCommodityAvailableToCountry(second, currentCountry?.id) ? 0 : 1;

      return firstRank - secondRank;
    });
  }, [commodities, searchTerm, i18n.language, showAll, currentCountry?.id]);

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 }
    );
  }, [resolvedSorting, searchTerm]);

  useEffect(() => {
    setPagination(currentPagination => {
      const pageCount = Math.max(
        1,
        Math.ceil(filteredCommodities.length / currentPagination.pageSize)
      );
      const nextPageIndex = Math.min(currentPagination.pageIndex, pageCount - 1);

      return nextPageIndex === currentPagination.pageIndex
        ? currentPagination
        : { ...currentPagination, pageIndex: nextPageIndex };
    });
  }, [filteredCommodities.length]);

  const table = useReactTable({
    columns,
    data: filteredCommodities,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: getCommodityRowId,
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: handleSortingChange,
    state: {
      pagination,
      sorting: resolvedSorting,
    },
  });

  if (errorMessage) {
    return <>{displayErrorMessage(errorMessage)}</>;
  }

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <DataTable
        emptyMessage={t("commodities.list.empty", "No commodities found")}
        isLoading={isFetching}
        table={table}
      />
      <DataTablePagination table={table} />
    </div>
  );
};
